import { Controller } from "@hotwired/stimulus"

// Manages amenities override for games (disable/modify season amenities, add game-specific ones)
export default class extends Controller {
  static targets = ["seasonAmenities", "additions", "output"]
  static values = { 
    seasonAmenities: Array,
    override: Object 
  }

  connect() {
    this.render()
  }

  render() {
    this.renderSeasonAmenities()
    this.renderAdditions()
    this.updateOutput()
  }

  renderSeasonAmenities() {
    const container = this.seasonAmenitiesTarget
    container.innerHTML = ""
    
    const disabled = this.overrideValue.disabled || []
    const overrides = this.overrideValue.overrides || {}

    this.seasonAmenitiesValue.forEach(amenity => {
      const isDisabled = disabled.includes(amenity.id)
      const override = overrides[amenity.id] || {}
      
      const row = document.createElement('div')
      row.className = 'flex items-center gap-4 p-3 bg-gray-50 dark:bg-white/5 rounded-lg'
      row.dataset.amenityId = amenity.id
      
      row.innerHTML = `
        <label class="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" 
                 data-action="change->amenities-override#toggleAmenity"
                 ${!isDisabled ? 'checked' : ''}
                 class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded">
          <span class="text-sm font-medium text-gray-900 dark:text-white">${this.escapeHtml(amenity.name)}</span>
        </label>
        <div class="flex-1">
          <input type="text" 
                 data-field="description"
                 data-action="input->amenities-override#updateOverride"
                 value="${this.escapeHtml(override.description || amenity.description || '')}"
                 placeholder="${this.escapeHtml(amenity.description || 'Description')}"
                 class="block w-full rounded-md bg-white px-3 py-1 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:bg-white/5 dark:text-white dark:outline-white/10 ${isDisabled ? 'opacity-50' : ''}">
        </div>
        <span class="text-xs text-gray-500 dark:text-gray-400 capitalize">${amenity.icon || 'tickets'}</span>
      `
      
      container.appendChild(row)
    })
  }

  renderAdditions() {
    const container = this.additionsTarget
    container.innerHTML = ""
    
    const additions = this.overrideValue.additions || []
    
    additions.forEach((amenity, index) => {
      this.addAdditionRow(amenity, index)
    })
  }

  addAdditionRow(amenity = null, index = null) {
    const container = this.additionsTarget
    const row = document.createElement('div')
    row.className = 'addition-row flex items-start gap-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg'
    row.dataset.additionIndex = index !== null ? index : (this.overrideValue.additions || []).length
    
    const id = amenity?.id || this.generateId()
    row.dataset.amenityId = id
    
    row.innerHTML = `
      <div class="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <input type="text" 
                 data-field="name"
                 data-action="input->amenities-override#updateAddition"
                 value="${this.escapeHtml(amenity?.name || '')}"
                 placeholder="Name"
                 class="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:bg-white/5 dark:text-white dark:outline-white/10">
        </div>
        <div>
          <input type="text" 
                 data-field="description"
                 data-action="input->amenities-override#updateAddition"
                 value="${this.escapeHtml(amenity?.description || '')}"
                 placeholder="Description"
                 class="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:bg-white/5 dark:text-white dark:outline-white/10">
        </div>
        <div>
          <select data-field="icon"
                  data-action="change->amenities-override#updateAddition"
                  class="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:*:bg-gray-800">
            <option value="tickets" ${amenity?.icon === 'tickets' ? 'selected' : ''}>Tickets</option>
            <option value="parking" ${amenity?.icon === 'parking' ? 'selected' : ''}>Parking</option>
            <option value="points" ${amenity?.icon === 'points' ? 'selected' : ''}>Points</option>
            <option value="club" ${amenity?.icon === 'club' ? 'selected' : ''}>Club Access</option>
            <option value="food" ${amenity?.icon === 'food' ? 'selected' : ''}>Food</option>
            <option value="drink" ${amenity?.icon === 'drink' ? 'selected' : ''}>Drink</option>
            <option value="vip" ${amenity?.icon === 'vip' ? 'selected' : ''}>VIP</option>
            <option value="star" ${amenity?.icon === 'star' ? 'selected' : ''}>Star</option>
            <option value="gift" ${amenity?.icon === 'gift' ? 'selected' : ''}>Gift</option>
          </select>
        </div>
      </div>
      <button type="button" 
              data-action="amenities-override#removeAddition"
              class="mt-1 p-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
        </svg>
      </button>
    `
    
    container.appendChild(row)
  }

  toggleAmenity(event) {
    const row = event.target.closest('[data-amenity-id]')
    const id = row.dataset.amenityId
    const isEnabled = event.target.checked
    
    let disabled = [...(this.overrideValue.disabled || [])]
    
    if (isEnabled) {
      disabled = disabled.filter(d => d !== id)
    } else {
      if (!disabled.includes(id)) {
        disabled.push(id)
      }
    }
    
    // Update description input opacity
    const descInput = row.querySelector('[data-field="description"]')
    if (descInput) {
      descInput.classList.toggle('opacity-50', !isEnabled)
    }
    
    this.overrideValue = { ...this.overrideValue, disabled }
    this.updateOutput()
  }

  updateOverride(event) {
    const row = event.target.closest('[data-amenity-id]')
    const id = row.dataset.amenityId
    const field = event.target.dataset.field
    const value = event.target.value
    
    const overrides = { ...(this.overrideValue.overrides || {}) }
    
    // Find the original amenity to check if value differs
    const originalAmenity = this.seasonAmenitiesValue.find(a => a.id === id)
    const originalValue = originalAmenity?.[field] || ''
    
    if (value && value !== originalValue) {
      overrides[id] = { ...(overrides[id] || {}), [field]: value }
    } else {
      // Remove override if value matches original
      if (overrides[id]) {
        delete overrides[id][field]
        if (Object.keys(overrides[id]).length === 0) {
          delete overrides[id]
        }
      }
    }
    
    this.overrideValue = { ...this.overrideValue, overrides }
    this.updateOutput()
  }

  addAmenity(event) {
    event.preventDefault()
    const additions = [...(this.overrideValue.additions || [])]
    const newAmenity = {
      id: this.generateId(),
      name: "",
      description: "",
      icon: "star"
    }
    additions.push(newAmenity)
    this.overrideValue = { ...this.overrideValue, additions }
    this.addAdditionRow(newAmenity, additions.length - 1)
    this.updateOutput()
  }

  updateAddition(event) {
    const row = event.target.closest('.addition-row')
    const index = parseInt(row.dataset.additionIndex)
    const field = event.target.dataset.field
    const value = event.target.value
    
    const additions = [...(this.overrideValue.additions || [])]
    if (additions[index]) {
      additions[index] = { ...additions[index], [field]: value }
      this.overrideValue = { ...this.overrideValue, additions }
      this.updateOutput()
    }
  }

  removeAddition(event) {
    event.preventDefault()
    const row = event.target.closest('.addition-row')
    const index = parseInt(row.dataset.additionIndex)
    
    const additions = [...(this.overrideValue.additions || [])]
    additions.splice(index, 1)
    this.overrideValue = { ...this.overrideValue, additions }
    
    row.remove()
    
    // Reindex remaining rows
    this.additionsTarget.querySelectorAll('.addition-row').forEach((r, i) => {
      r.dataset.additionIndex = i
    })
    
    this.updateOutput()
  }

  updateOutput() {
    // Clean up empty values
    const output = {}
    
    if (this.overrideValue.disabled?.length > 0) {
      output.disabled = this.overrideValue.disabled
    }
    
    if (this.overrideValue.overrides && Object.keys(this.overrideValue.overrides).length > 0) {
      output.overrides = this.overrideValue.overrides
    }
    
    if (this.overrideValue.additions?.length > 0) {
      // Filter out empty additions
      const validAdditions = this.overrideValue.additions.filter(a => a.name?.trim())
      if (validAdditions.length > 0) {
        output.additions = validAdditions
      }
    }
    
    this.outputTarget.value = JSON.stringify(output)
  }

  generateId() {
    return Math.random().toString(36).substring(2, 14)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text || ''
    return div.innerHTML
  }
}
